import { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Search, Filter, RefreshCw, Timer, AlertCircle } from "lucide-react"
import { Input, Select, SelectItem, Button, Card, CardBody, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Chip, Spinner, Tooltip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react"
import { useListTasksPaginated, useUpdateTaskStatus, useDeleteTask } from "@src/presentation/hooks/task"
import { useStats } from "@src/presentation/hooks/stats"
import type { TaskListItemDTO } from "@src/application/tasks/dto"
import { formatDateHuman } from "@src/shared/utils/date-formatter"
import { ToastService } from "@src/shared/services/toast.service"
import { useInView } from "react-intersection-observer"

export default function AllTasksPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState<string>("all")
    const [rescheduleModal, setRescheduleModal] = useState<{ isOpen: boolean; task: TaskListItemDTO | null }>({ isOpen: false, task: null })
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; task: TaskListItemDTO | null }>({ isOpen: false, task: null })

    // Intersection observer for infinite scroll
    const { ref, inView } = useInView();

    // Fetch tasks with pagination - pass status filter to API
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, refetch } = useListTasksPaginated({
        limit: 20,
        status: statusFilter === "all" ? undefined : statusFilter
    });

    // Fetch stats for summary cards
    const { data: statsData } = useStats();

    // Flatten the paginated data
    const tasks = data?.pages.flatMap((page: any) => page?.tasks || []) || [];

    // Use stats from API for summary cards
    const stats = useMemo(() => {
        if (!statsData) {
            return {
                total: 0,
                pending: 0,
                processing: 0,
                completed: 0,
                failed: 0,
                completionRate: '0',
            };
        }
        const jobStats = statsData.pasco_jobs;
        return {
            total: jobStats.total_jobs,
            pending: jobStats.status_breakdown.pending || 0,
            processing: jobStats.status_breakdown.processing || 0,
            completed: jobStats.status_breakdown.completed || 0,
            failed: jobStats.status_breakdown.failed || 0,
            completionRate: jobStats.total_jobs > 0
                ? ((jobStats.status_breakdown.completed || 0) / jobStats.total_jobs * 100).toFixed(1)
                : '0',
        };
    }, [statsData]);

    // Load more when in view
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const { mutate: updateStatus, isPending: isUpdating } = useUpdateTaskStatus({
        onSuccess: () => {
            setRescheduleModal({ isOpen: false, task: null })
            refetch()
            ToastService.success("Task has been rescheduled successfully")
        },
        onError: (error: Error) => {
            ToastService.error(error.message || "Failed to reschedule task")
        }
    })
    const { mutate: deleteTask, isPending: isDeleting } = useDeleteTask({
        onSuccess: () => {
            setDeleteModal({ isOpen: false, task: null })
            refetch()
            ToastService.success("Task has been deleted successfully")
        },
        onError: (error: Error) => {
            ToastService.error(error.message || "Failed to delete task")
        }
    })

    // Filter tasks based on search (status filtering is now done server-side)
    const filteredTasks = useMemo(() => {
        if (!Array.isArray(tasks)) {
            return [];
        }
        return tasks.filter((task) => {
            const matchesSearch =
                searchQuery === "" ||
                task.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                task.pascoId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                task.errorMessage?.toLowerCase().includes(searchQuery.toLowerCase())

            return matchesSearch
        })
    }, [tasks, searchQuery])

    const handleRescheduleClick = (task: TaskListItemDTO) => {
        setRescheduleModal({ isOpen: true, task })
    }

    const confirmReschedule = () => {
        if (rescheduleModal.task) {
            updateStatus({
                taskId: rescheduleModal.task.id,
                updateData: { status: "pending" }
            })
        }
    }

    const handleDeleteClick = (task: TaskListItemDTO) => {
        setDeleteModal({ isOpen: true, task })
    }

    const confirmDelete = () => {
        if (deleteModal.task) {
            deleteTask(deleteModal.task.id)
        }
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <Timer className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Background Tasks</h1>
                            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                                {filteredTasks.length} {filteredTasks.length === 1 ? "task" : "tasks"}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                color="default"
                                variant="flat"
                                onPress={() => refetch()}
                                isLoading={isLoading}
                                startContent={!isLoading ? <RefreshCw className="h-4 w-4" /> : undefined}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Refresh</span>
                                <span className="sm:hidden">Refresh</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Stats Overview */}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <Card className="rounded border border-border shadow-none bg-secondary">
                            <CardBody className="p-4">
                                <p className="text-sm text-default-500">Total</p>
                                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                            </CardBody>
                        </Card>
                        <Card className="border-warning/20 border bg-warning/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <p className="text-sm text-warning">Pending</p>
                                <p className="text-2xl font-bold text-warning">{stats.pending}</p>
                            </CardBody>
                        </Card>
                        <Card className="border-primary/20 border bg-primary/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <p className="text-sm text-primary">Processing</p>
                                <p className="text-2xl font-bold text-primary">{stats.processing}</p>
                            </CardBody>
                        </Card>
                        <Card className="border-success/20 border bg-success/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <p className="text-sm text-success">Completed</p>
                                <p className="text-2xl font-bold text-success">{stats.completed}</p>
                            </CardBody>
                        </Card>
                        <Card className="border-danger/20 border bg-danger/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <p className="text-sm text-danger">Failed</p>
                                <p className="text-2xl font-bold text-danger">{stats.failed}</p>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Search and Filters */}
                    <Card className="border border-border bg-card rounded shadow-none hover:shadow-custom transition-shadow">
                        <CardBody className="p-4">
                            <div className="flex flex-col gap-4">
                                {/* Search */}
                                <Input
                                    type="text"
                                    placeholder="Search by task ID, PASCO ID, or error message..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    startContent={<Search className="h-4 w-4 text-default-400" />}
                                    classNames={{
                                        input: "text-sm",
                                        inputWrapper: "h-10 rounded",
                                    }}
                                />

                                {/* Filters Row */}
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <Filter className="h-4 w-4 text-default-400" />
                                        <span className="text-sm text-default-500">Filters:</span>
                                    </div>

                                    <Select
                                        label="Status"
                                        placeholder="Select status"
                                        selectedKeys={[statusFilter]}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        size="sm"
                                        className="w-40"
                                        classNames={{
                                            trigger: "h-10 border border-border rounded",
                                            popoverContent: "rounded border border-border",
                                        }}
                                    >
                                        <SelectItem key="all" className="rounded">All Status</SelectItem>
                                        <SelectItem key="pending" className="rounded">Pending</SelectItem>
                                        <SelectItem key="processing" className="rounded">Processing</SelectItem>
                                        <SelectItem key="completed" className="rounded">Completed</SelectItem>
                                        <SelectItem key="failed" className="rounded">Failed</SelectItem>
                                    </Select>
                                </div>

                                {/* Active Filters Summary */}
                                {(searchQuery || statusFilter !== "all") && (
                                    <div className="flex items-center gap-2 border-t border-divider pt-3">
                                        <span className="text-sm text-default-500">Active filters:</span>
                                        <div className="flex flex-wrap gap-2 items-center">
                                            {searchQuery && (
                                                <Chip
                                                    onClose={() => setSearchQuery("")}
                                                    variant="flat"
                                                    color="primary"
                                                    size="sm"
                                                >
                                                    Search: {searchQuery}
                                                </Chip>
                                            )}
                                            {statusFilter !== "all" && (
                                                <Chip
                                                    onClose={() => setStatusFilter("all")}
                                                    variant="flat"
                                                    color="primary"
                                                    size="sm"
                                                >
                                                    Status: {statusFilter}
                                                </Chip>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="light"
                                                color="default"
                                                className="rounded"
                                                onPress={() => {
                                                    setSearchQuery("")
                                                    setStatusFilter("all")
                                                }}
                                            >
                                                Clear all
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardBody>
                    </Card>

                    {/* Tasks Table */}
                    <Card className="border border-border rounded shadow-none bg-card hover:shadow-custom transition-shadow">
                        <CardBody className="p-0">
                            {isLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Spinner size="lg" />
                                </div>
                            ) : error ? (
                                <div className="flex flex-col items-center justify-center p-8 text-danger">
                                    <AlertCircle className="h-12 w-12 mb-2" />
                                    <p>Error loading tasks: {error.message}</p>
                                </div>
                            ) : filteredTasks.length === 0 ? (
                                <div className="flex flex-col items-center justify-center p-8 text-default-500">
                                    <Timer className="h-12 w-12 mb-2" />
                                    <p>No tasks found</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table aria-label="Tasks table" classNames={{
                                        th: 'rounded',
                                        wrapper: 'rounded',
                                    }}>
                                        <TableHeader>
                                            <TableColumn>TASK ID</TableColumn>
                                            <TableColumn className="hidden md:table-cell">PASCO ID</TableColumn>
                                            <TableColumn className="hidden md:table-cell">ATTEMPTS</TableColumn>
                                            <TableColumn className="hidden lg:table-cell">STATUS</TableColumn>
                                            <TableColumn className="hidden md:table-cell">ERROR</TableColumn>
                                            <TableColumn className="hidden md:table-cell">CREATED</TableColumn>
                                            <TableColumn className="hidden md:table-cell">UPDATED</TableColumn>
                                            <TableColumn>ACTIONS</TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredTasks.map((task: TaskListItemDTO) => {
                                                return (
                                                    <TableRow key={task.id} className="border-y border-border min-h-[64px]">
                                                        <TableCell>
                                                            <Tooltip content={task.id}>
                                                                <code className="text-xs cursor-pointer">
                                                                    {task.id.substring(0, 8)}...
                                                                </code>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell className="hidden lg:table-cell">
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                onPress={() => navigate(`/pascos/${task.pascoId}`)}
                                                                className="text-xs"
                                                            >
                                                                {task.pascoId.substring(0, 8)}...
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell className="hidden lg:table-cell">
                                                            <Chip size="sm" variant="flat">
                                                                {task.attemptCount}
                                                            </Chip>
                                                        </TableCell>
                                                        <TableCell className="hidden lg:table-cell">
                                                            <Chip
                                                                size="sm"
                                                                color={
                                                                    task.status === 'completed' ? 'success' :
                                                                        task.status === 'failed' ? 'danger' :
                                                                            task.status === 'processing' ? 'primary' :
                                                                                task.status === 'pending' ? 'warning' : 'default'
                                                                }
                                                                variant="flat"
                                                            >
                                                                {task.status}
                                                            </Chip>
                                                        </TableCell>
                                                        <TableCell className="hidden md:table-cell">
                                                            {task.errorMessage ? (
                                                                <Tooltip content={task.errorMessage}>
                                                                    <span className="text-xs text-danger cursor-pointer">
                                                                        {task.errorMessage.substring(0, 30)}...
                                                                    </span>
                                                                </Tooltip>
                                                            ) : (
                                                                <span className="text-xs text-default-400">-</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="hidden md:table-cell">
                                                            <span className="text-xs">{formatDateHuman(task.createdAt)}</span>
                                                        </TableCell>
                                                        <TableCell className="hidden md:table-cell">
                                                            <span className="text-xs">{formatDateHuman(task.updatedAt)}</span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <Button
                                                                    size="sm"
                                                                    variant="flat"
                                                                    onPress={() => navigate(`/tasks/${task.id}`)}
                                                                    className="rounded"
                                                                >
                                                                    View
                                                                </Button>
                                                                {task.status === "failed" && (
                                                                    <Button
                                                                        size="sm"
                                                                        color="warning"
                                                                        variant="flat"
                                                                        onPress={() => handleRescheduleClick(task)}
                                                                        className="rounded"
                                                                    >
                                                                        Reschedule
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    size="sm"
                                                                    color="danger"
                                                                    variant="flat"
                                                                    onPress={() => handleDeleteClick(task)}
                                                                    className="rounded"
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {/* Infinite Scroll Trigger - Always rendered for proper intersection detection */}
                            <div ref={ref} className="h-4" />

                            {/* Loading skeletons when fetching next page */}
                            {isFetchingNextPage && (
                                <div className="mt-4">
                                    <Card className="border border-border rounded shadow-none">
                                        <CardBody className="p-4">
                                            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                                                Loading more tasks...
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            )}

                            {!hasNextPage && tasks.length > 0 && (
                                <div className="mt-4 text-center text-sm text-muted-foreground py-6">
                                    You've reached the end
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </main>

            {/* Reschedule Confirmation Modal */}
            <Modal
                isOpen={rescheduleModal.isOpen}
                onClose={() => setRescheduleModal({ isOpen: false, task: null })}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1 border-b border-border">
                        <h3 className="text-lg font-semibold">Confirm Reschedule</h3>
                    </ModalHeader>
                    <ModalBody className="py-6">
                        <div className="space-y-4">
                            <p className="text-sm text-default-600">
                                Are you sure you want to reschedule this task? This will reset the task status to <strong className="text-warning">pending</strong> and it will be re-processed.
                            </p>
                            {rescheduleModal.task && (
                                <div className="rounded border border-border bg-default-50 p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">Task ID:</span>
                                        <code className="text-xs">{rescheduleModal.task.id.substring(0, 16)}...</code>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">PASCO ID:</span>
                                        <code className="text-xs">{rescheduleModal.task.pascoId.substring(0, 16)}...</code>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">Current Status:</span>
                                        <Chip size="sm" color="danger" variant="flat">{rescheduleModal.task.status}</Chip>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">Attempts:</span>
                                        <span>{rescheduleModal.task.attemptCount}</span>
                                    </div>
                                    {rescheduleModal.task.errorMessage && (
                                        <div className="pt-2 border-t border-border">
                                            <span className="text-xs text-default-500">Error:</span>
                                            <p className="text-xs text-danger mt-1">{rescheduleModal.task.errorMessage}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter className="border-t border-border">
                        <Button
                            variant="light"
                            onPress={() => setRescheduleModal({ isOpen: false, task: null })}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="warning"
                            onPress={confirmReschedule}
                            isLoading={isUpdating}
                            className="rounded"
                        >
                            Reschedule Task
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, task: null })}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1 border-b border-border">
                        <h3 className="text-lg font-semibold text-danger">Confirm Deletion</h3>
                    </ModalHeader>
                    <ModalBody className="py-6">
                        <div className="space-y-4">
                            <p className="text-sm text-default-600">
                                Are you sure you want to <strong className="text-danger">permanently delete</strong> this task? This action cannot be undone.
                            </p>
                            {deleteModal.task && (
                                <div className="rounded border border-danger/20 bg-danger/5 p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">Task ID:</span>
                                        <code className="text-xs">{deleteModal.task.id.substring(0, 16)}...</code>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">PASCO ID:</span>
                                        <code className="text-xs">{deleteModal.task.pascoId.substring(0, 16)}...</code>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-default-500">Status:</span>
                                        <Chip size="sm" color="default" variant="flat">
                                            {deleteModal.task.status}
                                        </Chip>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter className="border-t border-border">
                        <Button
                            variant="light"
                            onPress={() => setDeleteModal({ isOpen: false, task: null })}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            onPress={confirmDelete}
                            isLoading={isDeleting}
                            className="rounded"
                        >
                            Delete Task
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}
